import { createContext, useContext, useEffect } from "react";
import { resolvePageMeta } from "./pageMeta.js";

// A render-local collector, never shared between build routes or requests.
export const PageMetaContext = createContext(null);

function setMeta(attribute, key, value) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!value) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", value);
}

function removeJsonLd() {
  document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((script) => script.remove());
}

export default function usePageMeta(options) {
  const collect = useContext(PageMetaContext);
  const meta = resolvePageMeta(options);
  collect?.(meta);
  const serialised = JSON.stringify(meta);
  useEffect(() => {
    const current = JSON.parse(serialised);
    document.title = current.title;
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", current.canonical);
    Object.entries(current.names).forEach(([key, value]) => setMeta("name", key, value));
    Object.entries(current.properties).forEach(([key, value]) => setMeta("property", key, value));
    removeJsonLd();
    current.jsonLd.forEach((value) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      script.textContent = JSON.stringify(value);
      document.head.appendChild(script);
    });
    return removeJsonLd;
  }, [serialised]);
}
